import { useEffect, useState } from "react";

import { AtlasFigure } from "@/components/AtlasFigure";
import { EntryDetailPanel } from "@/components/EntryDetailPanel";
import { SectionIntro } from "@/components/SectionIntro";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { toast } from "@/components/ui/sonner";
import { fetchAtlasNodes, fetchLibraryItem, trackExploration } from "@/lib/api";
import { pickText } from "@/lib/content";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export const AtlasPage = ({ language, vipEmail }) => {
  const [nodes, setNodes] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [error, setError] = useState("");
  const isMobile = useMediaQuery("(max-width: 1023px)");

  useEffect(() => {
    const loadNodes = async () => {
      try {
        setLoading(true);
        const data = await fetchAtlasNodes();
        setNodes(data);
        if (data[0]?.slug) setSelectedSlug(data[0].slug);
        setError("");
      } catch (error) {
        console.error(error);
        setError(pickText(language, "The atlas is unavailable right now.", "L'atlante non è disponibile in questo momento."));
        toast.error(pickText(language, "The atlas could not load.", "L'atlante non può essere caricato."));
      } finally {
        setLoading(false);
      }
    };

    loadNodes();
  }, [language]);

  useEffect(() => {
    const loadEntry = async () => {
      if (!selectedSlug) return;
      try {
        const data = await fetchLibraryItem(selectedSlug);
        setSelectedEntry(data);

        // Track exploration for VIP achievements
        if (vipEmail && data) {
          const category = data.category || (data.sefira ? "sefira" : data.chakra ? "chakra" : data.tattva ? "tattva" : null);
          if (category) {
            try {
              const result = await trackExploration({
                email: vipEmail,
                entry_slug: selectedSlug,
                category: category,
              });
              if (result.newly_unlocked?.length > 0) {
                toast.success(pickText(language, "🏆 Achievement unlocked!", "🏆 Achievement sbloccato!"));
              }
            } catch (trackError) {
              // Silently fail exploration tracking
              console.log("Exploration tracking skipped:", trackError);
            }
          }
        }
      } catch (error) {
        console.error(error);
        setSelectedEntry(null);
        toast.error(pickText(language, "The selected correspondence could not load.", "La corrispondenza selezionata non può essere caricata."));
      }
    };

    loadEntry();
  }, [selectedSlug, language, vipEmail]);

  const handleSelect = (slug) => {
    setSelectedSlug(slug);
    if (isMobile) setMobileOpen(true);
  };

  return (
    <div className="space-y-10" data-testid="atlas-page">
      <SectionIntro
        description={pickText(
          language,
          "Click any luminous point to reveal its symbolic role, body location, and cross-system correspondences.",
          "Clicca qualsiasi punto luminoso per rivelarne il ruolo simbolico, la posizione corporea e le corrispondenze tra sistemi.",
        )}
        eyebrow={pickText(language, "Interactive Atlas", "Atlante Interattivo")}
        testId="atlas-intro"
        title={pickText(language, "Trace the temple from crown to earth.", "Traccia il tempio dalla corona alla terra.")}
      />

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]" data-testid="atlas-layout-grid">
        <div className="space-y-4">
          <AtlasFigure language={language} nodes={nodes} onSelect={handleSelect} selectedSlug={selectedSlug} />
          {error ? (
            <div className="glass-card rounded-[2rem] border-red-500/20 p-5 text-sm text-red-200" data-testid="atlas-error-state">
              {error}
            </div>
          ) : null}
          <div className="glass-card rounded-[2rem] p-5 text-sm text-slate-300" data-testid="atlas-helper-card">
            {loading
              ? pickText(language, "Loading atlas nodes…", "Caricamento nodi dell'atlante…")
              : pickText(
                  language,
                  "On mobile, tapping a node opens the detail drawer. On larger screens, the detail panel stays visible beside the map.",
                  "Su mobile, toccare un nodo apre il cassetto dei dettagli. Su schermi grandi, il pannello dei dettagli rimane visibile accanto alla mappa.",
                )}
          </div>
        </div>

        <div className="hidden xl:block" data-testid="atlas-desktop-detail-column">
          <div className="sticky top-32">
            <EntryDetailPanel entry={selectedEntry} language={language} />
          </div>
        </div>
      </div>

      <div className="xl:hidden" data-testid="atlas-mobile-preview">
        <EntryDetailPanel entry={selectedEntry} language={language} showNotes={false} />
      </div>

      <Drawer onOpenChange={setMobileOpen} open={mobileOpen && isMobile}>
        <DrawerContent className="max-h-[88vh] rounded-t-[2rem] border-white/10 bg-[#0A0A12] text-white">
          <DrawerHeader>
            <DrawerTitle data-testid="atlas-mobile-drawer-title">
              {pickText(language, selectedEntry?.title_en || "Selected node", selectedEntry?.title_it || "Nodo selezionato")}
            </DrawerTitle>
            <DrawerDescription data-testid="atlas-mobile-drawer-description">
              {pickText(language, "Expanded correspondence details.", "Dettagli estesi della corrispondenza.")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-6">
            <EntryDetailPanel entry={selectedEntry} language={language} />
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};